import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Trophy, MapPin, Crown, Medal, Award, Archive, History, Eye, Trash2, Calendar, User as UserIcon, Target } from 'lucide-react';
import RankingSection from './zone-sections/RankingSection';
import BranchTargetsSection from '@/components/BranchTargetsSection';
import BarberGoalsRankingSection from './zone-sections/BarberGoalsRankingSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function AdminZonesRanking() {
  const { getAuthHeaders } = useAuth();
  const [zones, setZones] = useState([]);
  const [activeZoneId, setActiveZoneId] = useState(null);
  const [rankingByZone, setRankingByZone] = useState({});
  const [salesByZone, setSalesByZone] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current');
  // Month selector for "Mes Actual" tab (defaults to current month in es-MX)
  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey);
  const monthOptions = useMemo(() => {
    const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    const out = [];
    const now = new Date();
    for (let i = 0; i < 24; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      out.push({ value: key, label: `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}` });
    }
    return out;
  }, []);
  const isPastMonth = selectedMonth !== currentMonthKey;
  // Archive dialog
  const [archiveDialog, setArchiveDialog] = useState(null); // { zone, allowForce }
  const [archiving, setArchiving] = useState(false);
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewSnapshot, setViewSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const zonesRes = await axios.get(`${API}/zones`, getAuthHeaders());
      const zonesList = zonesRes.data || [];
      setZones(zonesList);

      if (zonesList.length === 0) {
        setLoading(false);
        return;
      }

      const monthQuery = selectedMonth && selectedMonth !== currentMonthKey ? `&month=${selectedMonth}` : '';
      const results = await Promise.all(
        zonesList.map(z =>
          axios
            .get(`${API}/zones/dashboard/ranking?zone_id=${z.id}${monthQuery}`, getAuthHeaders())
            .then(r => [z.id, r.data])
            .catch(() => [z.id, null])
        )
      );
      const map = {};
      results.forEach(([id, data]) => { map[id] = data; });
      setRankingByZone(map);
      setActiveZoneId(prev => prev || zonesList[0].id);
    } catch (e) {
      toast.error('Error al cargar rankings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedMonth, currentMonthKey]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/zones/dashboard/ranking/history`, getAuthHeaders());
      setHistory(res.data?.snapshots || []);
    } catch (e) {
      toast.error('Error al cargar histórico');
    } finally {
      setHistoryLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchHistory]);

  // Lazy-load sales dashboard for the active zone when the targets tab is opened
  useEffect(() => {
    if (activeTab !== 'targets' || !activeZoneId) return;
    const cacheKey = `${activeZoneId}__${selectedMonth}`;
    if (salesByZone[cacheKey] !== undefined) return; // cached (null or data)
    setSalesLoading(true);
    const monthQuery = selectedMonth && selectedMonth !== currentMonthKey ? `&month=${selectedMonth}` : '';
    axios
      .get(`${API}/zones/dashboard/sales?zone_id=${activeZoneId}${monthQuery}`, getAuthHeaders())
      .then(r => setSalesByZone(prev => ({ ...prev, [cacheKey]: r.data })))
      .catch(() => setSalesByZone(prev => ({ ...prev, [cacheKey]: null })))
      .finally(() => setSalesLoading(false));
  }, [activeTab, activeZoneId, salesByZone, getAuthHeaders, selectedMonth, currentMonthKey]);

  const salesCacheKey = activeZoneId ? `${activeZoneId}__${selectedMonth}` : null;

  const handleArchive = async (force = false) => {
    if (!archiveDialog?.zone) return;
    setArchiving(true);
    try {
      await axios.post(
        `${API}/zones/dashboard/ranking/archive?zone_id=${archiveDialog.zone.id}${force ? '&force=true' : ''}`,
        {},
        getAuthHeaders()
      );
      toast.success(force ? 'Snapshot sobrescrito' : 'Snapshot archivado');
      setArchiveDialog(null);
      if (activeTab === 'history') fetchHistory();
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        // Show force option
        setArchiveDialog(prev => ({ ...prev, allowForce: true, conflictMsg: e.response?.data?.detail }));
      } else {
        toast.error(e.response?.data?.detail || 'Error al archivar');
      }
    } finally {
      setArchiving(false);
    }
  };

  const openSnapshot = async (snapshotId) => {
    setSnapshotLoading(true);
    try {
      const res = await axios.get(`${API}/zones/dashboard/ranking/history/${snapshotId}`, getAuthHeaders());
      setViewSnapshot(res.data);
    } catch (e) {
      toast.error('Error al cargar snapshot');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const deleteSnapshot = async (snapshotId) => {
    if (!window.confirm('¿Eliminar este snapshot permanentemente?')) return;
    try {
      await axios.delete(`${API}/zones/dashboard/ranking/history/${snapshotId}`, getAuthHeaders());
      toast.success('Snapshot eliminado');
      fetchHistory();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-zinc-500">Cargando rankings...</div>
      </AdminLayout>
    );
  }

  if (zones.length === 0) {
    return (
      <AdminLayout>
        <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-12 text-center max-w-2xl" data-testid="empty-zones">
          <MapPin className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">Aún no hay zonas gerenciales creadas</p>
          <p className="text-zinc-600 text-xs mt-2">Crea zonas en &quot;Zonas Gerenciales&quot; para ver sus rankings aquí.</p>
        </Card>
      </AdminLayout>
    );
  }

  const activeRanking = activeZoneId ? rankingByZone[activeZoneId] : null;
  const activeZone = activeZoneId ? zones.find(z => z.id === activeZoneId) : null;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-7xl" data-testid="admin-zones-ranking">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl text-white tracking-tight flex items-center gap-3">
            <Trophy className="w-7 h-7 text-gold" />
            Ranking por Zona Gerencial
          </h1>
          <p className="text-zinc-500 mt-1">
            Visualiza el desempeño de cada zona y archiva snapshots mensuales para histórico
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900/50 border border-zinc-800" data-testid="ranking-tabs">
            <TabsTrigger value="current" data-testid="tab-current">
              <Trophy className="w-4 h-4 mr-1.5" />
              Mes Actual
            </TabsTrigger>
            <TabsTrigger value="targets" data-testid="tab-targets">
              <Target className="w-4 h-4 mr-1.5" />
              Metas por Sucursal
            </TabsTrigger>
            <TabsTrigger value="barber-goals" data-testid="tab-barber-goals">
              <Target className="w-4 h-4 mr-1.5" />
              Avance Metas Barberos
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-1.5" />
              Histórico
            </TabsTrigger>
          </TabsList>

          {/* ============ CURRENT MONTH TAB ============ */}
          <TabsContent value="current" className="space-y-6 mt-6">
            {/* Zone Tabs */}
            <div className="flex gap-1 bg-zinc-900/50 border border-zinc-800 rounded-sm p-1 w-fit overflow-x-auto" data-testid="zones-tabs">
              {zones.map(z => (
                <button
                  key={z.id}
                  onClick={() => setActiveZoneId(z.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm whitespace-nowrap transition-colors ${
                    activeZoneId === z.id ? 'bg-gold/20 text-gold' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`zone-tab-${z.id}`}
                >
                  <MapPin className="w-4 h-4" />
                  {z.name}
                  <span className="ml-1 text-[10px] text-zinc-500">
                    ({z.branches?.length || 0} sucursal{z.branches?.length === 1 ? '' : 'es'})
                  </span>
                </button>
              ))}
            </div>

            {/* Active Zone Info Bar */}
            {activeZone && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="active-zone-info">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-gold/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="text-sm text-white font-semibold">{activeZone.name}</p>
                      <p className="text-xs text-zinc-500">
                        Gerente: {activeZone.manager_name || 'Sin asignar'} ·{' '}
                        {activeZone.branches?.length || 0} sucursal{activeZone.branches?.length === 1 ? '' : 'es'}
                        {activeRanking?.month && ` · ${activeRanking.month}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger
                        className="w-[200px] bg-zinc-950 border-zinc-800 text-white"
                        data-testid="ranking-month-select"
                      >
                        <Calendar className="w-4 h-4 mr-2 text-gold" />
                        <SelectValue placeholder="Mes" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-80">
                        {monthOptions.map((m) => (
                          <SelectItem key={m.value} value={m.value} className="text-white">
                            {m.label}
                            {m.value === currentMonthKey && (
                              <span className="ml-2 text-[10px] text-gold">· Actual</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isPastMonth && (
                      <Button
                        onClick={() => setArchiveDialog({ zone: activeZone, allowForce: false })}
                        className="bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30"
                        data-testid={`archive-btn-${activeZone.id}`}
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Cerrar mes y archivar
                      </Button>
                    )}
                  </div>
                </div>
                {isPastMonth && (
                  <p className="text-[11px] text-zinc-500 italic mt-3 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-gold" />
                    Visualizando un mes histórico (solo lectura). El archivo solo está disponible en el mes actual.
                  </p>
                )}
              </Card>
            )}

            {/* Ranking content for active zone */}
            {activeRanking ? (
              <>
                <TopThreePodium ranking={activeRanking} />
                <RankingSection ranking={activeRanking} />
              </>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 p-8 text-center">
                <p className="text-zinc-500">Sin datos disponibles para esta zona</p>
              </Card>
            )}
          </TabsContent>

          {/* ============ TARGETS PER BRANCH TAB ============ */}
          <TabsContent value="targets" className="space-y-4 mt-6" data-testid="targets-tab-content">
            {/* Zone Tabs + Month Selector */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-1 bg-zinc-900/50 border border-zinc-800 rounded-sm p-1 w-fit overflow-x-auto" data-testid="zones-tabs-targets">
                {zones.map(z => (
                  <button
                    key={z.id}
                    onClick={() => setActiveZoneId(z.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm whitespace-nowrap transition-colors ${
                      activeZoneId === z.id ? 'bg-gold/20 text-gold' : 'text-zinc-400 hover:text-white'
                    }`}
                    data-testid={`zone-tab-targets-${z.id}`}
                  >
                    <MapPin className="w-4 h-4" />
                    {z.name}
                  </button>
                ))}
              </div>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger
                  className="w-[200px] bg-zinc-950 border-zinc-800 text-white"
                  data-testid="targets-month-select"
                >
                  <Calendar className="w-4 h-4 mr-2 text-gold" />
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-80">
                  {monthOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value} className="text-white">
                      {m.label}
                      {m.value === currentMonthKey && (
                        <span className="ml-2 text-[10px] text-gold">· Actual</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Zone summary bar */}
            {activeZoneId && salesByZone[salesCacheKey] && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="targets-zone-summary">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-gold/20 flex items-center justify-center">
                      <Target className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="text-sm text-white font-semibold">
                        {salesByZone[salesCacheKey].zone_name}
                        {salesByZone[salesCacheKey].goal_breakdown?.is_past_month && (
                          <span className="ml-2 text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">
                            Histórico · {salesByZone[salesCacheKey].goal_breakdown?.month_label}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-zinc-500">
                        Meta agregada: <span className="text-white font-medium">{fmt(salesByZone[salesCacheKey].monthly_target)}</span>
                        {salesByZone[salesCacheKey].monthly_target_source === 'auto_sum' && (
                          <span className="ml-2 text-[10px] bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-400">Auto-suma</span>
                        )}
                        {salesByZone[salesCacheKey].monthly_target_source === 'manual' && (
                          <span className="ml-2 text-[10px] bg-gold/10 px-1.5 py-0.5 rounded text-gold">Manual</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <p className="text-zinc-500">Suma sucursales</p>
                    <p className="text-white font-semibold">
                      {fmt(salesByZone[salesCacheKey].branch_targets_sum?.total || 0)}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      Serv {fmt(salesByZone[salesCacheKey].branch_targets_sum?.services || 0)} · Prod {fmt(salesByZone[salesCacheKey].branch_targets_sum?.products || 0)}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Branch targets grid */}
            {salesLoading && !salesByZone[salesCacheKey] ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-8 text-center text-zinc-500">
                Cargando metas por sucursal...
              </Card>
            ) : !salesByZone[salesCacheKey] ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-8 text-center text-zinc-500">
                Sin datos disponibles para esta zona.
              </Card>
            ) : (salesByZone[salesCacheKey].by_branch || []).every(b => (b.targets?.total || 0) === 0) ? (
              <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-12 text-center" data-testid="empty-targets">
                <Target className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">Sin metas por sucursal configuradas para esta zona</p>
                <p className="text-zinc-600 text-xs mt-2">
                  Configúralas en &quot;Zonas Gerenciales&quot; editando la zona &rarr; sección &quot;Metas por Sucursal&quot;.
                </p>
              </Card>
            ) : (
              <BranchTargetsSection byBranch={salesByZone[salesCacheKey].by_branch} />
            )}
          </TabsContent>

          {/* ============ BARBER GOALS RANKING TAB ============ */}
          <TabsContent value="barber-goals" className="space-y-4 mt-6" data-testid="barber-goals-tab-content">
            <BarberGoalsRankingSection getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* ============ HISTORY TAB ============ */}
          <TabsContent value="history" className="space-y-4 mt-6" data-testid="history-tab-content">
            {historyLoading ? (
              <div className="text-center text-zinc-500 py-12">Cargando histórico...</div>
            ) : history.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-12 text-center" data-testid="empty-history">
                <History className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">Aún no hay snapshots archivados</p>
                <p className="text-zinc-600 text-xs mt-2">
                  Usa el botón &quot;Cerrar mes y archivar&quot; en la pestaña Mes Actual para crear el primer snapshot.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {history.map((s) => (
                  <Card key={s.id} className="bg-zinc-900/50 border-zinc-800 p-4 hover:border-zinc-700 transition-colors" data-testid={`history-item-${s.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-white font-semibold flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gold" />
                          {s.zone_name}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                          <Calendar className="w-3 h-3" /> Mes: {s.month}
                        </p>
                        <p className="text-[10px] text-zinc-600 mt-0.5 flex items-center gap-1.5">
                          <UserIcon className="w-3 h-3" />
                          Por {s.archived_by_name || 'N/A'} · {s.archived_at ? new Date(s.archived_at).toLocaleDateString('es-MX') : ''}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openSnapshot(s.id)}
                          className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                          data-testid={`view-snapshot-${s.id}`}
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteSnapshot(s.id)}
                          className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
                          data-testid={`delete-snapshot-${s.id}`}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Mini preview */}
                    {s.top_3_branches?.length > 0 && (
                      <div className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-2 mb-2">
                        <p className="text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Top Sucursal</p>
                        <p className="text-sm text-white truncate">
                          {s.top_3_branches[0].branch_name} —{' '}
                          <span className="text-gold">{fmt(s.top_3_branches[0].revenue)}</span>
                        </p>
                      </div>
                    )}
                    {s.top_3_collaborators?.length > 0 && (
                      <div className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-2">
                        <p className="text-[10px] text-zinc-500 mb-1 uppercase tracking-wider">Top Colaborador</p>
                        <p className="text-sm text-white truncate">
                          {s.top_3_collaborators[0].barber_name} —{' '}
                          <span className="text-blue-400">{fmt(s.top_3_collaborators[0].total_revenue)}</span>
                        </p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Archive Confirmation Dialog */}
      <Dialog open={!!archiveDialog} onOpenChange={(o) => !o && setArchiveDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-gold" />
              {archiveDialog?.allowForce ? 'Snapshot ya existente' : 'Cerrar mes y archivar'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {archiveDialog?.allowForce
                ? archiveDialog?.conflictMsg
                : `Guarda permanentemente el ranking actual de "${archiveDialog?.zone?.name || ''}" como snapshot histórico del mes ${rankingByZone[archiveDialog?.zone?.id]?.month || ''}. Podrás consultarlo en la pestaña Histórico.`}
            </DialogDescription>
          </DialogHeader>

          <div className="text-xs text-zinc-400 bg-zinc-950/60 border border-zinc-800 rounded-sm p-3">
            <p className="mb-1.5"><span className="text-zinc-500">Zona:</span> <span className="text-white">{archiveDialog?.zone?.name}</span></p>
            <p className="mb-1.5"><span className="text-zinc-500">Mes:</span> <span className="text-white">{rankingByZone[archiveDialog?.zone?.id]?.month || '-'}</span></p>
            <p><span className="text-zinc-500">Sucursales:</span> <span className="text-white">{archiveDialog?.zone?.branches?.length || 0}</span></p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setArchiveDialog(null)}
              className="text-zinc-400"
              data-testid="cancel-archive-btn"
            >
              Cancelar
            </Button>
            {archiveDialog?.allowForce ? (
              <Button
                onClick={() => handleArchive(true)}
                disabled={archiving}
                className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/40"
                data-testid="force-archive-btn"
              >
                {archiving ? 'Sobrescribiendo...' : 'Sobrescribir snapshot'}
              </Button>
            ) : (
              <Button
                onClick={() => handleArchive(false)}
                disabled={archiving}
                className="bg-gold text-black hover:bg-gold/90"
                data-testid="confirm-archive-btn"
              >
                {archiving ? 'Archivando...' : 'Archivar ahora'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Snapshot Dialog */}
      <Dialog open={!!viewSnapshot} onOpenChange={(o) => !o && setViewSnapshot(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="view-snapshot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gold" />
              {viewSnapshot?.zone_name} — {viewSnapshot?.month}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Snapshot archivado el {viewSnapshot?.archived_at ? new Date(viewSnapshot.archived_at).toLocaleString('es-MX') : ''}
              {viewSnapshot?.archived_by_name && ` por ${viewSnapshot.archived_by_name}`}
            </DialogDescription>
          </DialogHeader>

          {snapshotLoading && <div className="text-center text-zinc-500 py-8">Cargando...</div>}

          {viewSnapshot?.snapshot && (
            <div className="space-y-4">
              <TopThreePodium ranking={viewSnapshot.snapshot} />
              <RankingSection ranking={viewSnapshot.snapshot} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

/* =================== Top 3 Podium =================== */
function TopThreePodium({ ranking }) {
  const topBranches = ranking?.top_3_branches || [];
  const topCollabs = ranking?.top_3_collaborators || [];
  const excluded = ranking?.excluded_collaborators_count || 0;

  const rankIcon = (idx) => {
    if (idx === 0) return <Crown className="w-5 h-5 text-gold" />;
    if (idx === 1) return <Medal className="w-5 h-5 text-zinc-300" />;
    return <Award className="w-5 h-5 text-orange-600" />;
  };
  const rankColor = (idx) => {
    if (idx === 0) return 'bg-gradient-to-br from-gold/20 to-zinc-900/50 border-gold/40';
    if (idx === 1) return 'bg-gradient-to-br from-zinc-400/15 to-zinc-900/50 border-zinc-400/40';
    return 'bg-gradient-to-br from-orange-600/15 to-zinc-900/50 border-orange-600/40';
  };
  const rankLabel = (idx) => (idx === 0 ? '1° Lugar' : idx === 1 ? '2° Lugar' : '3° Lugar');

  return (
    <div className="space-y-4" data-testid="top-three-podium">
      {excluded > 0 && (
        <div className="text-[11px] text-zinc-500 italic flex items-center gap-1.5">
          <Trophy className="w-3 h-3 text-gold" />
          {excluded} colaborador{excluded === 1 ? '' : 'es'} excluido{excluded === 1 ? '' : 's'} del ranking (configurado en el perfil)
        </div>
      )}

      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="top-3-branches-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-gold" />
          Top 3 Sucursales del Mes
        </h3>
        {topBranches.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">Sin datos</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {topBranches.map((b, idx) => (
              <div
                key={b.branch_id}
                className={`rounded-sm border p-4 ${rankColor(idx)}`}
                data-testid={`top3-branch-${idx}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {rankIcon(idx)}
                  <span className="text-xs text-zinc-400 uppercase tracking-wider">{rankLabel(idx)}</span>
                </div>
                <p className="text-lg font-bold text-white truncate">{b.branch_name}</p>
                <p className="text-2xl font-bold text-gold mt-1">{fmt(b.revenue)}</p>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {b.transactions} transacciones · Ticket prom. {fmt(b.avg_ticket)}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="top-3-collaborators-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-blue-400" />
          Top 3 Colaboradores del Mes
        </h3>
        {topCollabs.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">Aún sin ventas registradas este mes</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {topCollabs.map((c, idx) => (
              <div
                key={c.barber_id}
                className={`rounded-sm border p-4 ${rankColor(idx)}`}
                data-testid={`top3-collaborator-${idx}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {rankIcon(idx)}
                  <span className="text-xs text-zinc-400 uppercase tracking-wider">{rankLabel(idx)}</span>
                </div>
                <p className="text-lg font-bold text-white truncate">{c.barber_name}</p>
                <p className="text-[10px] text-zinc-500 mb-2">
                  {c.branch_name} {c.role_type === 'stylist' ? '· Estilista' : ''}
                </p>
                <p className="text-2xl font-bold text-blue-400">{fmt(c.total_revenue)}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                  <span>Servicios: <span className="text-green-400">{fmt(c.services_revenue)}</span></span>
                  <span>Productos: <span className="text-blue-300">{fmt(c.products_revenue)}</span></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
